import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { Card, CardContent } from "@mui/material";
import { BroadbandProviderQuestion } from "./BroadbandProviderQuestion/BroadbandProviderQuestion";
import { BroadbandCardHeader } from "./BroadbandCardHeader/BroadbandCardHeader";
import { BroadbandErrorContent } from "./BroadbandErrorContent/BroadbandErrorContent";
import { BroadbandPendingContent } from "./BroadbandLoadingContent/BroadbandPendingContent";
import { BroadbandUanForm } from "./BroadbandUanForm/BroadbandUanForm";
import { ErrorHelperText } from "@common/ErrorHelperText/ErrorHelperText";
import { useBroadbandCard } from "./useBroadbandCard";
import { ICart, IService } from "@features/cart/ICart";
import { useTranslation } from "react-i18next";
import { useStyle } from "./style";
import { cls } from "itsf-ui-common";

interface IParams {
    onDelete?: () => void;
    prequalId: string;
    offerName: string;
    offerId: number;
    services: IService[];
    onAnswerChange?: () => void;
    setCart: Dispatch<SetStateAction<ICart | undefined>>;
}

export const BroadbandCard = (props: IParams) => {
    const { onDelete, onAnswerChange, offerId } = props;
    const { t } = useTranslation("cart");
    const { classes } = useStyle();

    const [editTrigger, setEditTrigger] = useState(false);
    const handleEditClick = useCallback(() => setEditTrigger(true), []);

    const [isUanFormExpanded, setIsUanFormExpanded] = useState(false);

    const {
        nleError,
        isPending,
        ascertain,
        isLoadingAscertain,
        uan,
        saveAscertain,
        isLoadingSave,
        saveAscertainError,
        mutateAscertain,
        hasAvailableSlot,
        hasActiveBroadband,
        hasNoActiveBroadband,
        determineBroadbandKey,
        setCart,
        offerName,
        providerRadioValue,
        setProviderRadioValue,
        handleAgreeConfirmed,
        broadbandValidFieldName,
        broadbandHiddenInputRef,
        shouldForceValidateUanForm,
        shouldForceValidateProviderQuestion,
        errorCardClassName,
        broadbandErrorMessage,
        showBroadbandError,
    } = useBroadbandCard(props);

    const getStatusMessage = useCallback(() => {
        if (hasActiveBroadband) {
            if (uan) {
                return (
                    <>
                        {t("broadband.UAN.accountNumberLabel")} <span className={classes.uanBold}>{uan}</span>
                    </>
                );
            }

            return t("broadband.activeFound");
        }

        return null;
    }, [uan, hasActiveBroadband, t, classes.uanBold]);

    const isLoading = isPending || isLoadingAscertain;

    return (
        <>
            <input name={broadbandValidFieldName} ref={broadbandHiddenInputRef as never} type="hidden" />
            <Card className={errorCardClassName} elevation={2}>
                <CardContent
                    className={cls(
                        classes.headerContent,
                        isUanFormExpanded ? classes.headerContentRoundedBottom : undefined
                    )}
                >
                    <BroadbandCardHeader
                        deleteLabel={t("broadband.deleteLabel")}
                        errorBannerMessage={nleError && t("broadband.error.banner")}
                        hasError={Boolean(nleError)}
                        showEditIcon={Boolean(uan)}
                        statusMessage={getStatusMessage()}
                        title={offerName}
                        onDelete={onDelete}
                        onEdit={handleEditClick}
                    />
                </CardContent>
                {isLoading && (
                    <CardContent className={classes.loadingContent}>
                        <BroadbandPendingContent />
                    </CardContent>
                )}
                {!isLoading && (
                    <>
                        {(nleError || (hasNoActiveBroadband && !hasAvailableSlot)) && (
                            <CardContent className={classes.errorContent}>
                                <BroadbandErrorContent
                                    broadbandKeyTranslation={determineBroadbandKey()}
                                    onRemove={onDelete}
                                />
                            </CardContent>
                        )}
                        {hasNoActiveBroadband && hasAvailableSlot && (
                            <CardContent className={classes.headerAccordion}>
                                <BroadbandProviderQuestion
                                    ascertain={ascertain}
                                    isAscertainLoading={isLoadingAscertain}
                                    offerId={offerId}
                                    radioValue={providerRadioValue}
                                    setCart={setCart}
                                    setRadioValue={setProviderRadioValue}
                                    shouldForceValidate={shouldForceValidateProviderQuestion}
                                    onAgreeConfirmed={handleAgreeConfirmed}
                                    onAnswerChange={onAnswerChange}
                                />
                            </CardContent>
                        )}
                        {hasActiveBroadband && (
                            <BroadbandUanForm
                                ascertain={ascertain}
                                editTrigger={editTrigger}
                                error={saveAscertainError}
                                isLoading={isLoadingSave}
                                mutateAscertain={mutateAscertain}
                                saveAscertain={saveAscertain}
                                shouldForceValidate={shouldForceValidateUanForm}
                                onAccordionExpandedChange={setIsUanFormExpanded}
                                onAnswerChange={onAnswerChange}
                                onDelete={onDelete}
                                onEditHandled={() => setEditTrigger(false)}
                            />
                        )}
                    </>
                )}
            </Card>
            {showBroadbandError && <ErrorHelperText isCardError message={broadbandErrorMessage} />}
        </>
    );
};
